import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AppShell from './components/AppShell';
import api from './services/api';
import { safeDecode } from './utils/decode';
import { SystemAlerts, SystemChip } from './quotas/QuotaTable';
import { quotaKey, SYSTEM_LABELS } from './quotas/trackerSeries';
import useSurveyQuotas from './quotas/useSurveyQuotas';

const MakeQuotaView = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [quotaViewName, setQuotaViewName] = useState('');
  const { quotas: allQuotas, systems, error: quotasError } = useSurveyQuotas(surveyId);
  const [checkedDescriptions, setCheckedDescriptions] = useState([]);
  const [checkedSystems, setCheckedSystems] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [partnerChoice, setPartnerChoice] = useState(null);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));
  }, [surveyId]);

  const systemsPresent = [...new Set(allQuotas.map((q) => q.system))];
  const uniqueDescriptions = [...new Set(allQuotas.map((q) => q.description))];

  const filtered = allQuotas.filter((q) => {
    const systemMatch = checkedSystems.length === 0 || checkedSystems.includes(q.system);
    const descMatch = checkedDescriptions.length === 0 || checkedDescriptions.includes(q.description);
    const nameMatch = nameFilter === '' || q.name.toLowerCase().includes(nameFilter.toLowerCase());
    return systemMatch && descMatch && nameMatch;
  });

  // Partners see one system's quotas. Alchemer by default, unless the view has only Wrangler quotas.
  const countIn = (system) => filtered.filter((q) => q.system === system).length;
  const partnerSystem = partnerChoice
    ?? (countIn('alchemer') === 0 && countIn('wrangler') > 0 ? 'wrangler' : 'alchemer');

  const toggle = (setter) => (value) => (e) => setter((prev) => (
    e.target.checked ? [...prev, value] : prev.filter((v) => v !== value)
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    try {
      await api.post('/quota-monitors/', {
        survey: surveyId,
        quota_view_name: quotaViewName,
        quota_refs: filtered.map((q) => ({ system: q.system, id: q.id })),
        partner_system: partnerSystem,
      });
    } catch (err) {
      const data = err?.response?.data || {};
      setSaveError(Object.values(data).flat().join(' ') || "Couldn't create the quota view.");
      return;
    }
    alert(`Quota view "${quotaViewName}" created.`);
    navigate(`/survey/${surveyId}/quota-monitoring`);
  };

  if (!survey) return null;

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/quota-monitoring`} style={{ textDecoration: 'none', color: 'inherit' }}>Quota Monitoring</Link>
        <Typography color="textPrimary">Make a Quota View</Typography>
      </Breadcrumbs>

      <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ mb: 4 }}>
        Make a Quota View
      </Typography>

      <SystemAlerts systems={systems} />
      {quotasError && <Alert severity="error" sx={{ mb: 2 }}>{quotasError}</Alert>}

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 4,
          mb: 3,
        }}
      >
        <TextField
          label="Quota View Name"
          value={quotaViewName}
          onChange={(e) => setQuotaViewName(e.target.value)}
          fullWidth
          required
          sx={{ mb: 3 }}
        />

        {systemsPresent.length > 1 && (
          <>
            <Typography variant="h6" gutterBottom>Filter by System</Typography>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {systemsPresent.map((system) => (
                <FormControlLabel
                  key={system}
                  control={<Checkbox checked={checkedSystems.includes(system)} onChange={toggle(setCheckedSystems)(system)} />}
                  label={SYSTEM_LABELS[system] || system}
                />
              ))}
            </Box>
          </>
        )}

        <Typography variant="h6" gutterBottom>Filter by Description</Typography>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {uniqueDescriptions.map((desc, i) => (
            <FormControlLabel
              key={i}
              control={
                <Checkbox
                  value={desc}
                  checked={checkedDescriptions.includes(desc)}
                  onChange={toggle(setCheckedDescriptions)(desc)}
                />
              }
              label={safeDecode(desc || 'No description')}
            />
          ))}
        </Box>

        <Typography variant="h6" gutterBottom>Filter by Name</Typography>
        <TextField
          label="Filter text"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" gutterBottom>Partners see</Typography>
        <FormControl size="small" sx={{ minWidth: 260, mb: 1 }}>
          <InputLabel id="partners-see">Partners see</InputLabel>
          <Select labelId="partners-see" label="Partners see" value={partnerSystem} onChange={(e) => setPartnerChoice(e.target.value)}>
            {['alchemer', 'wrangler'].map((system) => (
              <MenuItem key={system} value={system}>{SYSTEM_LABELS[system]} quotas ({countIn(system)})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Partners see only these quotas, with nothing that tells them there are two systems. Stack users see them all.
          {countIn(partnerSystem) === 0 && filtered.length > 0
            && ` This view has no ${SYSTEM_LABELS[partnerSystem]} quotas, so partners would see "No quotas to show".`}
        </Typography>

        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!quotaViewName || filtered.length === 0}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Create Quota View
        </Button>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
        }}
      >
        <Typography variant="h6" gutterBottom>Quotas to include ({filtered.length})</Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                <TableCell>System</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Completes</TableCell>
                <TableCell>Limit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((q) => (
                <TableRow key={quotaKey(q)}>
                  <TableCell><SystemChip system={q.system} /></TableCell>
                  <TableCell>{q.id}</TableCell>
                  <TableCell>{safeDecode(q.name)}</TableCell>
                  <TableCell>{q.description || 'N/A'}</TableCell>
                  <TableCell>{q.responses}</TableCell>
                  <TableCell>{q.limit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AppShell>
  );
};

export default MakeQuotaView;
